/**
 * @fileoverview Calendar utility functions
 * @description Date math helpers for the calendar grid
 */

import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  startOfDay, endOfDay,
  addDays, addMonths, subMonths, addWeeks, subWeeks,
  format, isSameDay, isSameMonth, isWithinInterval, parseISO,
  differenceInDays, eachDayOfInterval,
} from 'date-fns';

/**
 * Get the grid of days to display for a month view
 * (includes leading/trailing days from previous/next month)
 */
export const getMonthGrid = (date) => {
  const monthStart = startOfMonth(date);
  const monthEnd = endOfMonth(date);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
  return eachDayOfInterval({ start: gridStart, end: gridEnd });
};

/**
 * Get the 7 days for a week view
 */
export const getWeekDays = (date) => {
  const weekStart = startOfWeek(date, { weekStartsOn: 0 });
  return eachDayOfInterval({ start: weekStart, end: addDays(weekStart, 6) });
};

/**
 * Get 24-hour slots for day view
 */
export const getHourSlots = () => {
  return Array.from({ length: 24 }, (_, i) => i);
};

/**
 * Navigate to previous/next period based on view
 */
export const navigateDate = (currentDate, view, direction) => {
  switch (view) {
    case 'month':
      return direction === 'prev' ? subMonths(currentDate, 1) : addMonths(currentDate, 1);
    case 'week':
      return direction === 'prev' ? subWeeks(currentDate, 1) : addWeeks(currentDate, 1);
    case 'day':
      return direction === 'prev' ? addDays(currentDate, -1) : addDays(currentDate, 1);
    default:
      return currentDate;
  }
};

/**
 * Get the display title for current view
 */
export const getViewTitle = (date, view) => {
  switch (view) {
    case 'month':
      return format(date, 'MMMM yyyy');
    case 'week': {
      const weekStart = startOfWeek(date, { weekStartsOn: 0 });
      const weekEnd = addDays(weekStart, 6);
      return `${format(weekStart, 'MMM d')} – ${format(weekEnd, 'MMM d, yyyy')}`;
    }
    case 'day':
      return format(date, 'EEEE, MMMM d, yyyy');
    default:
      return format(date, 'MMMM yyyy');
  }
};

/**
 * Check if an event falls on a given day
 */
export const isEventOnDay = (event, day) => {
  try {
    const eventStart = typeof event.start_date === 'string' ? parseISO(event.start_date) : event.start_date;
    const eventEnd = typeof event.end_date === 'string' ? parseISO(event.end_date) : event.end_date;
    const dayStart = startOfDay(day);
    const dayEnd = endOfDay(day);
    
    return (
      isWithinInterval(dayStart, { start: startOfDay(eventStart), end: endOfDay(eventEnd) }) ||
      isWithinInterval(eventStart, { start: dayStart, end: dayEnd })
    );
  } catch {
    return false;
  }
};

/**
 * Get events for a specific day
 */
export const getEventsForDay = (events, day) => {
  return events.filter((event) => isEventOnDay(event, day));
};

/**
 * Format a date for API submission
 */
export const formatForApi = (date) => {
  return format(date, "yyyy-MM-dd'T'HH:mm:ss");
};

/**
 * Format a date for display
 */
export const formatDisplay = (dateStr) => {
  try {
    const date = typeof dateStr === 'string' ? parseISO(dateStr) : dateStr;
    return format(date, 'MMM d, yyyy');
  } catch {
    return dateStr || '';
  }
};

/**
 * Format a date-time for display
 */
export const formatDisplayDateTime = (dateStr) => {
  try {
    const date = typeof dateStr === 'string' ? parseISO(dateStr) : dateStr;
    return format(date, 'MMM d, yyyy h:mm a');
  } catch {
    return dateStr || '';
  }
};

/**
 * Get duration text between two dates
 */
export const getDurationText = (startStr, endStr) => {
  try {
    const start = typeof startStr === 'string' ? parseISO(startStr) : startStr;
    const end = typeof endStr === 'string' ? parseISO(endStr) : endStr;
    const days = differenceInDays(end, start) + 1;
    if (days === 1) return '1 day';
    if (days < 7) return `${days} days`;
    const weeks = Math.floor(days / 7);
    const remaining = days % 7;
    if (remaining === 0) return weeks === 1 ? '1 week' : `${weeks} weeks`;
    return `${weeks}w ${remaining}d`;
  } catch {
    return '';
  }
};

/**
 * Compute event spans for merged calendar view (Teams-like)
 * Events maintain consistent row positions across all weeks based on their ID order
 * @param {Array} events - All events in the view range
 * @param {Array} days - Array of Date objects for the visible grid
 * @param {number} maxVisibleRows - Maximum event rows to show per week row
 * @returns {Object} { spans: Array, overflowByDay: Object }
 */
export const computeEventSpans = (events, days, maxVisibleRows = 3) => {
  if (!events.length || !days.length) {
    return { spans: [], overflowByDay: {} };
  }

  const spans = [];
  const overflowByDay = {};
  const totalWeeks = Math.ceil(days.length / 7);

  // Create a stable global row assignment based on event ID (first appearance order)
  // This ensures the same event always appears in the same row across all weeks
  const uniqueEventIds = [];
  const seenIds = new Set();
  events.forEach((evt) => {
    if (!seenIds.has(evt.id)) {
      seenIds.add(evt.id);
      uniqueEventIds.push(evt.id);
    }
  });

  // Map event ID to its global row index
  const globalRowMap = new Map();
  uniqueEventIds.forEach((id, index) => {
    globalRowMap.set(id, index);
  });

  // Process each week
  for (let weekIndex = 0; weekIndex < totalWeeks; weekIndex++) {
    const weekStart = weekIndex * 7;
    const weekDays = days.slice(weekStart, weekStart + 7);
    if (weekDays.length === 0) continue;

    // Get unique events for this week with their day spans
    const weekEventsMap = new Map();
    weekDays.forEach((day, dayIndex) => {
      events.forEach((evt) => {
        if (isEventOnDay(evt, day)) {
          if (!weekEventsMap.has(evt.id)) {
            weekEventsMap.set(evt.id, {
              event: evt,
              startDayIndex: dayIndex,
              endDayIndex: dayIndex,
              globalRow: globalRowMap.get(evt.id) || 0,
            });
          } else {
            weekEventsMap.get(evt.id).endDayIndex = dayIndex;
          }
        }
      });
    });

    // Sort by global row position (maintains consistent order across weeks)
    const weekEventsList = Array.from(weekEventsMap.values()).sort((a, b) => {
      return a.globalRow - b.globalRow;
    });

    // Track which local rows are occupied in this week
    const rowSlots = weekDays.map(() => new Array(maxVisibleRows + 10).fill(null));

    // Assign local rows while trying to maintain global order
    weekEventsList.forEach(({ event, startDayIndex, endDayIndex, globalRow }) => {
      // Try to assign to localRow = globalRow % maxVisibleRows first for consistency
      const preferredLocalRow = globalRow % maxVisibleRows;

      let assignedRow = -1;

      // First try the preferred row
      let canUsePreferred = true;
      for (let d = startDayIndex; d <= endDayIndex; d++) {
        if (rowSlots[d][preferredLocalRow] !== null) {
          canUsePreferred = false;
          break;
        }
      }

      if (canUsePreferred) {
        assignedRow = preferredLocalRow;
      } else {
        // Find first available row
        for (let row = 0; row < maxVisibleRows + 10; row++) {
          let rowIsFree = true;
          for (let d = startDayIndex; d <= endDayIndex; d++) {
            if (rowSlots[d][row] !== null) {
              rowIsFree = false;
              break;
            }
          }
          if (rowIsFree) {
            assignedRow = row;
            break;
          }
        }
      }

      if (assignedRow === -1) assignedRow = maxVisibleRows; // Overflow

      // Mark slots as occupied
      for (let d = startDayIndex; d <= endDayIndex; d++) {
        if (d < rowSlots.length) {
          rowSlots[d][assignedRow] = event.id;
        }
      }

      // Add to visible spans
      if (assignedRow < maxVisibleRows) {
        spans.push({
          event,
          weekIndex,
          rowIndex: assignedRow,
          startCol: startDayIndex + 1,
          endCol: endDayIndex + 2,
          spanDays: endDayIndex - startDayIndex + 1,
        });
      }
    });

    // Calculate overflow per day
    weekDays.forEach((day, dayIndex) => {
      const dayKey = format(day, 'yyyy-MM-dd');
      const occupiedSlots = rowSlots[dayIndex].filter(slot => slot !== null).length;
      const overflow = Math.max(0, occupiedSlots - maxVisibleRows);
      if (overflow > 0) {
        overflowByDay[dayKey] = overflow;
      }
    });
  }

  return { spans, overflowByDay };
};

export {
  format, parseISO, isSameDay, isSameMonth, startOfDay, endOfDay, addDays,
  startOfMonth, endOfMonth, startOfWeek,
};
